/**
 * Setzt Titel, Description und Canonical für clientseitig gerenderte Routen.
 *
 * Hintergrund: Die Rechtsseiten (/impressum, /datenschutz, /agb, /widerruf) sind
 * React-Routen ohne eigene Meta-Daten. Ohne diesen Hook trägt jede von ihnen den
 * Titel der Startseite UND deren `canonical` auf `/` — für Suchmaschinen sehen
 * sie damit wie Duplikate der Startseite aus.
 *
 * Suchmaschinen rendern JavaScript; der gerenderte DOM-Stand entscheidet daher
 * über Titel und Canonical. Dieses Modul ändert bewusst nur Meta-Daten, keine
 * Inhalte. Beim Verlassen einer Route wird über den Rückgabewert von useEffect
 * auf die Startseiten-Meta zurückgesetzt, damit kein Titel „kleben" bleibt.
 */

const SITE_ORIGIN = 'https://dsgvoeasy.de';
const BRAND_SUFFIX = ' | DSGVOeasy';

export interface PageMeta {
  /** Seitentitel; ohne `withBrandSuffix: false` wird die Marke ergänzt. */
  title: string;
  description: string;
  /** Pfad mit führendem Slash, z. B. "/impressum". */
  path: string;
  /** Standard true. Bei false wird `title` unverändert gesetzt. */
  withBrandSuffix?: boolean;
}

const DEFAULT_META: PageMeta = {
  title: 'DSGVOeasy – DSGVO-Compliance-Software ab 7,99 €/Monat',
  description:
    'Verarbeitungsverzeichnis, TOMs und Datenschutzhinweise in Minuten statt Stunden erstellen. 50+ Branchenvorlagen, kostenlos starten, monatlich kündbar.',
  path: '/',
  withBrandSuffix: false,
};

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (!el) {
    const tag = selector.startsWith('link') ? 'link' : 'meta';
    el = document.createElement(tag) as HTMLMetaElement | HTMLLinkElement;
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}

export function applyPageMeta({ title, description, path, withBrandSuffix = true }: PageMeta): void {
  if (typeof document === 'undefined') return;
  const fullTitle = withBrandSuffix ? `${title}${BRAND_SUFFIX}` : title;
  const url = `${SITE_ORIGIN}${path}`;

  document.title = fullTitle;
  upsertMeta('meta[name="description"]', { name: 'description', content: description });
  upsertMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
  upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });

  const canonical = document.head.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', url);
  } else {
    upsertMeta('link[rel="canonical"]', { rel: 'canonical', href: url });
  }
}

/** Stellt Titel/Description/Canonical der Startseite wieder her. */
export function resetPageMeta(): void {
  applyPageMeta(DEFAULT_META);
}
